import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Navigate, useSearchParams } from "react-router";
import type { RoundName } from "../../domain/round/types";
import { TournamentPicker } from "../../components/TournamentPicker";
import { api } from "../../lib/api";
import { persistRoundSelection } from "../../lib/roundSelection";
import { GeneratorSetupPanel } from "./GeneratorSetupPanel";
import { BritishMastersOverlapPanel } from "./BritishMastersOverlapPanel";
import { SharedGenerationStatus } from "./SharedGenerationStatus";
import { ManualBoardOperationsPage } from "../manual/ManualBoardOperationsPage";
import { RoundTransportPage } from "../transport/RoundTransportPage";
import { TransportEditorPage } from "../transport/TransportEditorPage";
import { CarBlockReorderPanel } from "../transport/CarBlockReorderPanel";
import { RoundExportPage } from "../reports/RoundExportPage";
import { RoundVersionsPage } from "../rounds/RoundVersionsPage";
import "./generator-workspace.css";
import "./generator-workspace-7tabs.css";
import "./generator-setup-v1.css";
import "./generator-tournament-picker.css";

type WorkspaceTab = "setup" | "board" | "transport" | "transport-edit" | "car-blocks" | "export" | "versions";
const ROUNDS: RoundName[] = ["R1", "R2", "R3", "R4"];
const TABS: Array<{ id: WorkspaceTab; step: string; label: string; description: string }> = [
  { id: "setup", step: "1", label: "Generator", description: "Dane, reguły i propozycje" },
  { id: "board", step: "2", label: "Rozpiska", description: "Edycja i walidacja" },
  { id: "transport", step: "3", label: "Transport", description: "Dojazd i powrót" },
  { id: "transport-edit", step: "4", label: "Edycja aut", description: "Kierowcy i pasażerowie" },
  { id: "car-blocks", step: "5", label: "Bloki aut", description: "Zmiany całych aut" },
  { id: "export", step: "6", label: "Eksport", description: "PNG i Excel" },
  { id: "versions", step: "7", label: "Wersje", description: "Warianty i approval" },
];
function isTab(value: string | null): value is WorkspaceTab { return TABS.some((item) => item.id === value); }
function parsedSeason(value: string | null) {
  const season = Number(value || new Date().getFullYear());
  return Number.isFinite(season) && season >= 2020 && season <= 2100 ? season : new Date().getFullYear();
}
function eventStatus(startDate?: string, endDate?: string) {
  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  if (startDate && endDate && startDate <= today && endDate >= today) return "TEN TYDZIEŃ";
  if (startDate && startDate > today) return "NADCHODZI";
  return "TURNIEJ";
}

async function nextOperationalRound(tournamentId: string): Promise<RoundName> {
  const workspaces = await Promise.all(ROUNDS.map(async (round) => {
    try { return await api.roundWorkspace(tournamentId, round); }
    catch { return null; }
  }));
  const firstOpen = workspaces.findIndex((workspace) => workspace && !workspace.confirmation.confirmed);
  if (firstOpen >= 0) return ROUNDS[firstOpen];
  const firstUnavailable = workspaces.findIndex((workspace) => !workspace);
  if (firstUnavailable >= 0) return ROUNDS[firstUnavailable];
  return "R4";
}

export function GeneratorWorkspacePage() {
  const [params, setParams] = useSearchParams();
  const requestedTab = params.get("tab");
  const active: WorkspaceTab = isTab(requestedTab) ? requestedTab : "setup";
  const current = useMemo(() => TABS.find((item) => item.id === active) || TABS[0], [active]);
  const activeIndex = TABS.findIndex((item) => item.id === active);
  const tournamentId = params.get("tournament") || "";
  const round = params.get("round") || "R1";
  const season = parsedSeason(params.get("season"));
  const tournaments = useQuery({ queryKey: ["tournaments", season], queryFn: () => api.tournaments(season), staleTime: 30_000 });
  const selectedTournament = (tournaments.data?.tournaments || []).find((item) => item.id === tournamentId);
  const isBritishMasters = tournamentId === "2026-british-masters";
  const [overlapOpen, setOverlapOpen] = useState(() => isBritishMasters);

  function select(tab: WorkspaceTab) {
    const next = new URLSearchParams(typeof window !== "undefined" ? window.location.search : params);
    if (tab === "setup") next.delete("tab"); else next.set("tab", tab);
    setParams(next, { replace: true });
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  async function openTournament(id: string, itemSeason: number) {
    let targetRound: RoundName = "R1";
    try { targetRound = await nextOperationalRound(id); }
    catch { targetRound = "R1"; }
    const next = new URLSearchParams();
    next.set("tournament", id);
    next.set("season", String(itemSeason));
    next.set("round", targetRound);
    setParams(next);
    persistRoundSelection(id, targetRound, itemSeason);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function leaveTournament() {
    const next = new URLSearchParams();
    next.set("season", String(season));
    setParams(next);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  if (requestedTab === "manual-build") {
    const next = new URLSearchParams(params);
    next.delete("tab");
    const search = next.toString();
    return <Navigate to={search ? `/manual?${search}` : "/manual"} replace />;
  }

  if (!tournamentId || (!tournaments.isPending && !selectedTournament)) {
    return (
      <section className="generator-landing">
        <header className="generator-landing-head">
          <div>
            <span className="eyebrow">OPERACJE / GENERATOR</span>
            <h2>Wybierz turniej do pracy</h2>
            <p>Aktywny tydzień jest zawsze pierwszy. Zakończone wydarzenia są schowane w archiwum, żeby nie konkurowały z bieżącą pracą.</p>
          </div>
          <label className="generator-season-control"><span>Sezon</span><select value={season} onChange={(event) => setParams({ season: event.target.value })}>{[2026, 2025, 2024].map((value) => <option key={value} value={value}>{value}</option>)}</select></label>
        </header>
        <TournamentPicker tournaments={tournaments.data?.tournaments || []} season={season} loading={tournaments.isPending} onOpen={openTournament} />
      </section>
    );
  }

  if (!selectedTournament) return <section className="generator-landing"><div className="premium-picker-state">Wczytuję wybrany turniej…</div></section>;

  const status = eventStatus(selectedTournament.startDate, selectedTournament.endDate);

  return <section className="generator-workspace-shell premium-generator-workspace">
    <header className="generator-command-header">
      <div className="generator-command-main">
        <div className="generator-command-badges"><span className={`generator-event-state ${status === "TEN TYDZIEŃ" ? "live" : ""}`}>{status}</span><span className="generator-round-badge">{round}</span></div>
        <h2>{selectedTournament.name}</h2>
        <p>{selectedTournament.startDate || ""}{selectedTournament.endDate ? ` — ${selectedTournament.endDate}` : ""} · etap {activeIndex + 1} z {TABS.length}: <strong>{current.label}</strong></p>
      </div>
      <div className="generator-command-actions">
        <div className="generator-current-step"><small>AKTYWNY ETAP</small><strong>{current.label}</strong><span>{current.description}</span></div>
        <button type="button" className="generator-change-tournament" onClick={leaveTournament}>← Zmień turniej</button>
      </div>
    </header>

    <SharedGenerationStatus />

    {isBritishMasters ? <>
      <aside className="generator-overlap-banner" aria-label="R1 do R2 overlap"><div><span className="generator-overlap-kicker">AWARYJNE · BRITISH MASTERS · R1 → R2</span><strong>Kontrola dostępności po opóźnionej R1</strong><p>Lista 35 pozycji R1 jest wpisana na stałe. SAFE +45 min jest ustawieniem docelowym; +30 min zostaje tylko jako awaryjne minimum.</p></div><button className="generator-overlap-button" type="button" onClick={() => setOverlapOpen((value) => !value)}>{overlapOpen ? "Ukryj tabelę R1 → R2" : "Pokaż tabelę R1 → R2"}</button></aside>
      {overlapOpen ? <BritishMastersOverlapPanel /> : null}
    </> : null}

    <nav className="generator-process-rail" aria-label="Generator rundy — workflow">
      {TABS.map((item, index) => <button key={item.id} type="button" className={`${active === item.id ? "active" : ""} ${index < activeIndex ? "visited" : ""}`} aria-current={active === item.id ? "step" : undefined} onClick={() => select(item.id)}>
        <span className="generator-process-index">{index < activeIndex ? "✓" : item.step}</span>
        <span className="generator-process-copy"><strong>{item.label}</strong><small>{item.description}</small></span>
      </button>)}
    </nav>

    <div className={`generator-workspace-content tab-${active}`}>
      {active === "setup" ? <GeneratorSetupPanel /> : null}
      {active === "board" ? <ManualBoardOperationsPage /> : null}
      {active === "transport" ? <RoundTransportPage /> : null}
      {active === "transport-edit" ? <TransportEditorPage /> : null}
      {active === "car-blocks" ? <CarBlockReorderPanel /> : null}
      {active === "export" ? <RoundExportPage /> : null}
      {active === "versions" ? <RoundVersionsPage /> : null}
    </div>
  </section>;
}
